import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { Excalidraw, exportToCanvas, restoreElements } from "@excalidraw/excalidraw";
import { v4 as uuidv4 } from "uuid";
import { buildApiUrl } from "../config";
import WhiteboardAITools from "./WhiteboardAITools";
import WhiteboardImagePanel, { fetchImageAsDataUrl } from "./WhiteboardImagePanel";
import WhiteboardTemplates from "./WhiteboardTemplates";

// ── helpers ──────────────────────────────────────────────
const blankPage = (label = "Page 1") => ({ id: uuidv4(), label, elements: [], files: {} });

const WhiteboardTab = () => {
  const teacherEmail = localStorage.getItem("teacherEmail");
  const [savedBoards, setSavedBoards]     = useState([]);
  const [title, setTitle]                 = useState("");
  const [selectedForm, setSelectedForm]   = useState("");
  const [forms, setForms]                 = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [isFullscreen, setIsFullscreen]   = useState(false);
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [showTemplates, setShowTemplates]   = useState(false);
  const [showToolsMenu, setShowToolsMenu]   = useState(false);
  // ── save / share modals ───────────────────────────────
  const [showSaveModal, setShowSaveModal]   = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [modalTitle, setModalTitle]         = useState("");
  const [modalForm, setModalForm]           = useState("");
  // ── unsaved-changes guard ─────────────────────────────
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // ── timer state ──────────────────────────────────────
  const [showTimer, setShowTimer]         = useState(false);
  const [timerMode, setTimerMode]         = useState("countdown");
  const [timerRunning, setTimerRunning]   = useState(false);
  const [timerSeconds, setTimerSeconds]   = useState(300);
  const [timerInput, setTimerInput]       = useState("5");
  const [timerFinished, setTimerFinished] = useState(false);
  const timerIntervalRef = useRef(null);

  // ── multi-page state ──────────────────────────────────
  const [pages, setPages]           = useState([blankPage("Page 1")]);
  const [currentPage, setCurrentPage] = useState(0);
  const pagesRef       = useRef([blankPage("Page 1")]);
  const currentPageRef = useRef(0);

  // keep refs in sync
  useEffect(() => { pagesRef.current = pages; },       [pages]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  // ── warn before page refresh / tab close ─────────────
  useEffect(() => {
    const handler = (e) => {
      if (!unsavedChanges) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [unsavedChanges]);

  const excalidrawRef    = useRef(null);
  const whiteboardRef    = useRef(null);
  const pendingDragImageRef = useRef(null);
  const whiteboardAIRef  = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);

  // ── snapshot current canvas into pages array ──────────
  const snapshotCurrentPage = useCallback(() => {
    if (!excalidrawAPI) return null;
    return {
      elements: excalidrawAPI.getSceneElements(),
      files:    excalidrawAPI.getFiles(),
    };
  }, [excalidrawAPI]);

  // ── page navigation ───────────────────────────────────
  const switchPage = useCallback((newIdx) => {
    if (!excalidrawAPI) return;
    const snap    = snapshotCurrentPage();
    const curIdx  = currentPageRef.current;
    const updated = pagesRef.current.map((p, i) =>
      i === curIdx ? { ...p, ...(snap || {}) } : p
    );
    pagesRef.current = updated;
    setPages(updated);
    setCurrentPage(newIdx);
    const target   = updated[newIdx];
    const restored = restoreElements(target.elements || [], null);
    excalidrawAPI.updateScene({ elements: restored, files: target.files || {} });
  }, [excalidrawAPI, snapshotCurrentPage]);

  const addPage = useCallback(() => {
    if (!excalidrawAPI) return;
    const snap   = snapshotCurrentPage();
    const curIdx = currentPageRef.current;
    const updated = pagesRef.current.map((p, i) =>
      i === curIdx ? { ...p, ...(snap || {}) } : p
    );
    const newPg    = blankPage(`Page ${updated.length + 1}`);
    const newPages = [...updated, newPg];
    const newIdx   = newPages.length - 1;
    pagesRef.current = newPages;
    setPages(newPages);
    setCurrentPage(newIdx);
    excalidrawAPI.updateScene({ elements: [], files: {} });
  }, [excalidrawAPI, snapshotCurrentPage]);

  const deletePage = useCallback((idx) => {
    const pg = pagesRef.current;
    if (pg.length <= 1) return;
    if (!window.confirm(`Delete "${pg[idx].label}"?`)) return;
    const newPages = pg.filter((_, i) => i !== idx);
    const curIdx   = currentPageRef.current;
    let   newIdx   = curIdx;
    if (idx === curIdx)    newIdx = Math.max(0, idx - 1);
    else if (idx < curIdx) newIdx = curIdx - 1;
    pagesRef.current = newPages;
    setPages(newPages);
    setCurrentPage(newIdx);
    const target   = newPages[newIdx];
    const restored = restoreElements(target.elements || [], null);
    excalidrawAPI.updateScene({ elements: restored, files: target.files || {} });
  }, [excalidrawAPI]);

  // ── image drag-drop helpers ───────────────────────────
  const insertImageAtCanvasPos = async (imgData, canvasX, canvasY) => {
    if (!excalidrawAPI) return;
    try {
      const { dataUrl, mimeType } = await fetchImageAsDataUrl(imgData.fullUrl || imgData.url);
      const fileId = uuidv4();
      excalidrawAPI.addFiles([{ id: fileId, dataURL: dataUrl, mimeType, created: Date.now() }]);
      const { restoreElements: restore } = await import("@excalidraw/excalidraw");
      const normalized = restore(
        [{ type: "image", id: fileId, x: canvasX - 150, y: canvasY - 100,
           width: 300, height: 200, fileId, status: "saved", scale: [1, 1], angle: 0 }],
        null
      );
      excalidrawAPI.updateScene({ elements: [...excalidrawAPI.getSceneElements(), ...normalized] });
    } catch (err) {
      console.error("Drop-insert failed:", err);
    }
  };

  const handleCanvasDrop = async (e) => {
    e.preventDefault();
    const img = pendingDragImageRef.current;
    pendingDragImageRef.current = null;
    if (!img || !excalidrawAPI) return;
    const rect = whiteboardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const state = excalidrawAPI.getAppState();
    const { scrollX, scrollY, zoom } = state;
    const canvasX = (e.clientX - rect.left - scrollX) / zoom.value;
    const canvasY = (e.clientY - rect.top  - scrollY) / zoom.value;
    await insertImageAtCanvasPos(img, canvasX, canvasY);
  };

  const handleCanvasDragOver = (e) => {
    if (pendingDragImageRef.current) e.preventDefault();
  };

  // ── image upload ──────────────────────────────────────
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(
        buildApiUrl("/api/whiteboard/upload-image"),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data.success) {
        return { dataURL: buildApiUrl(response.data.url), mimeType: file.type };
      }
      throw new Error("Upload failed");
    } catch {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ dataURL: reader.result, mimeType: file.type });
        reader.readAsDataURL(file);
      });
    }
  };

  // ── data fetching ─────────────────────────────────────
  const fetchForms = async () => {
    try {
      const res = await axios.get(buildApiUrl("/api/whiteboard/forms"));
      setForms(res.data);
    } catch { setForms([]); }
  };

  const fetchSavedBoards = async () => {
    if (!teacherEmail || teacherEmail === "null") return;
    try {
      const res = await axios.get(buildApiUrl(`/api/whiteboard/teacher/${encodeURIComponent(teacherEmail)}`));
      setSavedBoards(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchForms(); fetchSavedBoards(); }, []);

  // ── load / reset board ────────────────────────────────
  const loadBoard = (board) => {
    if (!excalidrawAPI) return;
    try {
      let loadedPages;
      if (board.pages && Array.isArray(board.pages) && board.pages.length > 0) {
        loadedPages = board.pages;
      } else {
        loadedPages = [{ id: uuidv4(), label: "Page 1", elements: board.elements || [], files: board.files || {} }];
      }
      const first    = loadedPages[0];
      const restored = restoreElements(first.elements || [], null);
      excalidrawAPI.updateScene({ elements: restored, files: first.files || {} });
      pagesRef.current = loadedPages;
      setPages(loadedPages);
      setCurrentPage(0);
      setTitle(board.title);
      setSelectedForm(board.form);
      setSelectedBoardId(board.id);
      setUnsavedChanges(false);
    } catch (err) {
      console.error("Error loading board:", err);
      alert("Failed to load board");
    }
  };

  const resetToNewBoard = () => {
    if (!excalidrawAPI) return;
    const blank = [blankPage("Page 1")];
    pagesRef.current = blank;
    setPages(blank);
    setCurrentPage(0);
    excalidrawAPI.updateScene({ elements: [], files: {} });
    setTitle("");
    setSelectedForm("");
    setSelectedBoardId(null);
    setUnsavedChanges(false);
  };

  // ── new board: check for unsaved work ────────────────
  const handleNewBoard = () => {
    const snap = snapshotCurrentPage();
    const hasContent = (snap?.elements || []).length > 0 ||
      pagesRef.current.some(p => (p.elements || []).length > 0);
    if (hasContent && unsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      resetToNewBoard();
    }
  };

  // ── save ──────────────────────────────────────────────
  const handleSave = async (assignToForm = false, titleOverride, formOverride) => {
    if (!excalidrawAPI) return;
    if (!teacherEmail || teacherEmail === "null") {
      alert("Unable to save: your session has expired. Please log in again.");
      return;
    }
    const effectiveTitle = titleOverride !== undefined ? titleOverride : title;
    const effectiveForm  = formOverride  !== undefined ? formOverride  : selectedForm;
    const snap     = snapshotCurrentPage();
    const curIdx   = currentPageRef.current;
    const allPages = pagesRef.current.map((p, i) =>
      i === curIdx ? { ...p, ...(snap || {}) } : p
    );
    const hasContent = allPages.some(p => (p.elements || []).length > 0);
    if (!hasContent || !effectiveTitle) {
      alert("Please draw something and enter a title.");
      return;
    }
    if (assignToForm && !effectiveForm) {
      alert("Please select a form to assign.");
      return;
    }
    try {
      await axios.post(buildApiUrl("/api/whiteboard/save"), {
        teacherEmail,
        title:    effectiveTitle,
        form:     assignToForm ? effectiveForm : "",
        elements: allPages[0].elements,
        files:    allPages[0].files || {},
        pages:    allPages,
      });
      alert(assignToForm ? "Saved & assigned!" : "Saved!");
      await fetchSavedBoards();
      resetToNewBoard();
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save.");
    }
  };

  // ── delete board ──────────────────────────────────────
  const deleteBoard = async (boardId) => {
    if (!window.confirm("Are you sure you want to delete this board?")) return;
    try {
      await axios.delete(buildApiUrl(`/api/whiteboard/${boardId}`));
      fetchSavedBoards();
      if (selectedBoardId === boardId) resetToNewBoard();
    } catch { alert("Failed to delete board."); }
  };

  // ── fullscreen ────────────────────────────────────────
  const toggleFullscreen = () => {
    const elem = whiteboardRef.current;
    if (!document.fullscreenElement) {
      (elem.requestFullscreen || elem.webkitRequestFullscreen || elem.msRequestFullscreen || elem.mozRequestFullScreen)
        ?.call(elem);
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen || document.mozCancelFullScreen)
        ?.call(document);
    }
  };

  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(!!(
        document.fullscreenElement       ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement     ||
        document.mozFullScreenElement
      ));
    };
    ["fullscreenchange","webkitfullscreenchange","msfullscreenchange","mozfullscreenchange"]
      .forEach(ev => document.addEventListener(ev, onChange));
    return () =>
      ["fullscreenchange","webkitfullscreenchange","msfullscreenchange","mozfullscreenchange"]
        .forEach(ev => document.removeEventListener(ev, onChange));
  }, []);

  // ── timer tick ────────────────────────────────────────
  useEffect(() => {
    if (timerRunning) {
      timerIntervalRef.current = setInterval(() => {
        setTimerSeconds(s => {
          if (timerMode === "countdown") {
            if (s <= 1) {
              setTimerRunning(false);
              setTimerFinished(true);
              return 0;
            }
            return s - 1;
          }
          return s + 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerIntervalRef.current);
  }, [timerRunning, timerMode]);

  const formatTime = (s) => {
    const h   = Math.floor(s / 3600);
    const m   = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
    return `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
  };

  // ── Timer Widget (shared between normal + fullscreen) ──
  const TimerWidget = showTimer ? (
    <div className={`flex flex-wrap items-center gap-2 px-3 py-1.5 border-b flex-shrink-0 ${
      timerFinished ? "bg-red-50" : "bg-amber-50"
    }`}>
      {/* Mode toggle */}
      <div className="flex rounded overflow-hidden border text-xs flex-shrink-0">
        <button
          onClick={() => { setTimerMode("countdown"); setTimerRunning(false); setTimerFinished(false); setTimerSeconds((parseInt(timerInput)||5)*60); }}
          className={`px-2 py-1 ${ timerMode==="countdown" ? "bg-amber-500 text-white" : "bg-white text-gray-600 hover:bg-gray-100" }`}>
          Countdown
        </button>
        <button
          onClick={() => { setTimerMode("stopwatch"); setTimerRunning(false); setTimerFinished(false); setTimerSeconds(0); }}
          className={`px-2 py-1 ${ timerMode==="stopwatch" ? "bg-amber-500 text-white" : "bg-white text-gray-600 hover:bg-gray-100" }`}>
          Stopwatch
        </button>
      </div>

      {/* Big time display */}
      <span className={`font-mono text-2xl font-bold w-24 text-center flex-shrink-0 ${
        timerFinished ? "text-red-600 animate-pulse"
          : timerMode==="countdown" && timerSeconds <= 10 && timerSeconds > 0 ? "text-orange-500"
          : "text-gray-800"
      }`}>
        {formatTime(timerSeconds)}
      </span>

      {/* Minutes input (countdown + not running) */}
      {timerMode === "countdown" && !timerRunning && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <input type="number" min="0" max="180" value={timerInput}
            onChange={(e) => {
              setTimerInput(e.target.value);
              setTimerSeconds((parseInt(e.target.value)||0)*60);
              setTimerFinished(false);
            }}
            className="border rounded px-2 py-1 w-14 text-sm text-center" />
          <span className="text-xs text-gray-500">min</span>
        </div>
      )}

      {/* Start / Pause */}
      <button
        onClick={() => { setTimerRunning(r => !r); setTimerFinished(false); }}
        className={`px-3 py-1 rounded text-sm font-medium flex-shrink-0 ${
          timerRunning ? "bg-yellow-400 hover:bg-yellow-500 text-gray-900" : "bg-green-500 hover:bg-green-600 text-white"
        }`}>
        {timerRunning ? "⏸ Pause" : "▶ Start"}
      </button>

      {/* Reset */}
      <button
        onClick={() => {
          setTimerRunning(false);
          setTimerFinished(false);
          setTimerSeconds(timerMode==="countdown" ? (parseInt(timerInput)||5)*60 : 0);
        }}
        className="px-3 py-1 rounded text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 flex-shrink-0">
        ↺ Reset
      </button>

      {timerFinished && (
        <span className="text-red-600 font-bold text-sm animate-pulse flex-shrink-0">⏰ Time&apos;s up!</span>
      )}
      <button
        onClick={() => { setTimerRunning(false); setShowTimer(false); }}
        className="ml-auto flex-shrink-0 text-gray-400 hover:text-gray-700 text-lg leading-none px-1"
        title="Close timer">
        ✕
      </button>
    </div>
  ) : null;

  // ── Page Carousel (shared between normal + fullscreen) ──
  const PageCarousel = (
    <div className="flex items-center gap-1 px-3 py-1 bg-gray-50 border-b overflow-x-auto flex-shrink-0">
      {pages.map((page, idx) => (
        <div key={page.id} className="flex items-center flex-shrink-0">
          <button
            onClick={() => switchPage(idx)}
            className={`px-3 py-1 rounded text-sm transition-colors border ${
              currentPage === idx
                ? "bg-white border-blue-500 font-semibold text-blue-700 shadow-sm"
                : "bg-gray-200 border-transparent hover:bg-gray-300 text-gray-600"
            }`}
          >
            {page.label}
          </button>
          {pages.length > 1 && (
            <button
              onClick={() => deletePage(idx)}
              className="ml-0.5 text-gray-400 hover:text-red-500 text-xs px-0.5 leading-none"
              title="Delete page"
            >
              x
            </button>
          )}
        </div>
      ))}
      <button
        onClick={addPage}
        className="ml-2 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-sm flex-shrink-0"
        title="Add new page"
      >
        + Page
      </button>
      <span className="ml-auto text-xs text-gray-400 flex-shrink-0 pr-1">
        {currentPage + 1} / {pages.length}
      </span>
    </div>
  );

  // ── render ────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen">

      {/* Normal-mode top bar */}
      {!isFullscreen && (
        <>
          <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-100 border-b">
            <button onClick={handleNewBoard}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded">
              + New Board
            </button>
            <select value={selectedBoardId || ""}
              onChange={(e) => {
                const board = savedBoards.find(b => b.id === parseInt(e.target.value));
                if (board) loadBoard(board);
              }}
              className="border px-3 py-2 rounded min-w-[250px]">
              <option value="">Select a board</option>
              {savedBoards.map(b => (
                <option key={b.id} value={b.id}>
                  {b.title} | {b.form || "None"} | {new Date(b.created_at).toLocaleDateString()}
                </option>
              ))}
            </select>
            <button
              onClick={() => { setModalTitle(title); setShowSaveModal(true); }}
              title="Save board"
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-lg leading-none">
              💾
            </button>
            <button
              onClick={() => { setModalTitle(title); setModalForm(selectedForm); setShowShareModal(true); }}
              title="Save & Assign"
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-lg leading-none">
              📤
            </button>
            {/* Full Excalidraw button hidden */}
            {/* ── Tools dropdown ── */}
            <div className="relative">
              <button
                onClick={() => setShowToolsMenu(v => !v)}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                  showToolsMenu ? "bg-gray-700 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                }`}>
                🛠 Tools <span className="text-xs">{showToolsMenu ? "▲" : "▼"}</span>
              </button>
              {showToolsMenu && (
                <div
                  className="absolute left-0 top-full mt-1 z-[9999] bg-white border rounded-xl shadow-xl w-56 py-1"
                  onMouseLeave={() => setShowToolsMenu(false)}
                >
                  <button
                    onClick={() => { setShowImagePanel(v => !v); setShowToolsMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 text-gray-700">
                    <span className="text-lg">🖼</span>
                    <span>{showImagePanel ? "Hide Image Panel" : "Image Panel"}</span>
                    {showImagePanel && <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">on</span>}
                  </button>
                  <button
                    onClick={() => { setShowTimer(v => !v); setShowToolsMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 text-gray-700">
                    <span className="text-lg">⏱</span>
                    <span>{showTimer ? "Hide Timer" : "Timer"}</span>
                    {showTimer && <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">on</span>}
                  </button>
                  <div className="border-t my-1" />
                  <button
                    onClick={() => { setShowTemplates(true); setShowToolsMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-teal-50 text-teal-700 font-medium">
                    <span className="text-lg">📐</span>
                    <span>Templates</span>
                  </button>
                  <div className="border-t my-1" />
                  <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">AI Tools</div>
                  <button
                    disabled={aiBusy}
                    onClick={() => { whiteboardAIRef.current?.triggerLesson(); setShowToolsMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-yellow-50 text-yellow-700 disabled:opacity-50">
                    <span className="text-lg">📋</span><span>Generate Lesson</span>
                  </button>
                  <button
                    disabled={aiBusy}
                    onClick={() => { whiteboardAIRef.current?.triggerDiagram(); setShowToolsMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-indigo-50 text-indigo-700 disabled:opacity-50">
                    <span className="text-lg">✨</span><span>Text → Diagram</span>
                  </button>
                  <button
                    disabled={aiBusy}
                    onClick={() => { whiteboardAIRef.current?.triggerOCR(); setShowToolsMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-purple-50 text-purple-700 disabled:opacity-50">
                    <span className="text-lg">✍</span><span>Handwriting → Text</span>
                  </button>
                  <button
                    disabled={aiBusy}
                    onClick={() => { whiteboardAIRef.current?.triggerMath(); setShowToolsMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-emerald-50 text-emerald-700 disabled:opacity-50">
                    <span className="text-lg">Σ</span><span>Solve Math</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          <WhiteboardAITools ref={whiteboardAIRef} excalidrawAPI={excalidrawAPI} hidden />
          {TimerWidget}
          {PageCarousel}
        </>
      )}

      {/* Canvas row */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {showImagePanel && !isFullscreen && (
          <WhiteboardImagePanel
            excalidrawAPI={excalidrawAPI}
            onDragStart={(img) => { pendingDragImageRef.current = img; }}
            onClose={() => setShowImagePanel(false)}
          />
        )}

        <div
          ref={whiteboardRef}
          className={`border shadow relative flex-1 ${
            isFullscreen ? "fixed inset-0 z-50 bg-white flex flex-col" : "flex flex-col"
          }`}
          onDrop={handleCanvasDrop}
          onDragOver={handleCanvasDragOver}
        >
          {/* Fullscreen overlay toolbar */}
          {isFullscreen && (
            <div className="flex-shrink-0 z-[9999] bg-white border-b shadow-sm">
              <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-gray-100 border-b">
                <button
                  onClick={() => { setModalTitle(title); setShowSaveModal(true); }}
                  title="Save board"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-lg leading-none">
                  💾
                </button>
                <button
                  onClick={() => { setModalTitle(title); setModalForm(selectedForm); setShowShareModal(true); }}
                  title="Save & Assign"
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-lg leading-none">
                  📤
                </button>
                {/* ── Tools dropdown (fullscreen) ── */}
                <div className="relative">
                  <button
                    onClick={() => setShowToolsMenu(v => !v)}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                      showToolsMenu ? "bg-gray-700 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                    }`}>
                    🛠 Tools <span className="text-xs">{showToolsMenu ? "▲" : "▼"}</span>
                  </button>
                  {showToolsMenu && (
                    <div
                      className="absolute left-0 top-full mt-1 z-[99999] bg-white border rounded-xl shadow-xl w-56 py-1"
                      onMouseLeave={() => setShowToolsMenu(false)}
                    >
                      <button
                        onClick={() => { setShowImagePanel(v => !v); setShowToolsMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 text-gray-700">
                        <span className="text-lg">🖼</span>
                        <span>{showImagePanel ? "Hide Image Panel" : "Image Panel"}</span>
                        {showImagePanel && <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">on</span>}
                      </button>
                      <button
                        onClick={() => { setShowTimer(v => !v); setShowToolsMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 text-gray-700">
                        <span className="text-lg">⏱</span>
                        <span>{showTimer ? "Hide Timer" : "Timer"}</span>
                        {showTimer && <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">on</span>}
                      </button>
                      <div className="border-t my-1" />
                      <button
                        onClick={() => { setShowTemplates(true); setShowToolsMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-teal-50 text-teal-700 font-medium">
                        <span className="text-lg">📐</span>
                        <span>Templates</span>
                      </button>
                      <div className="border-t my-1" />
                      <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">AI Tools</div>
                      <button
                        disabled={aiBusy}
                        onClick={() => { whiteboardAIRef.current?.triggerLesson(); setShowToolsMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-yellow-50 text-yellow-700 disabled:opacity-50">
                        <span className="text-lg">📋</span><span>Generate Lesson</span>
                      </button>
                      <button
                        disabled={aiBusy}
                        onClick={() => { whiteboardAIRef.current?.triggerDiagram(); setShowToolsMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-indigo-50 text-indigo-700 disabled:opacity-50">
                        <span className="text-lg">✨</span><span>Text → Diagram</span>
                      </button>
                      <button
                        disabled={aiBusy}
                        onClick={() => { whiteboardAIRef.current?.triggerOCR(); setShowToolsMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-purple-50 text-purple-700 disabled:opacity-50">
                        <span className="text-lg">✍</span><span>Handwriting → Text</span>
                      </button>
                      <button
                        disabled={aiBusy}
                        onClick={() => { whiteboardAIRef.current?.triggerMath(); setShowToolsMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-emerald-50 text-emerald-700 disabled:opacity-50">
                        <span className="text-lg">Σ</span><span>Solve Math</span>
                      </button>
                    </div>
                  )}
                </div>
                <button onClick={toggleFullscreen}
                  className="ml-auto px-4 py-1.5 bg-gray-800 text-white text-xs rounded hover:bg-gray-700">
                  ✕ Exit Fullscreen
                </button>
              </div>
              <WhiteboardAITools ref={whiteboardAIRef} excalidrawAPI={excalidrawAPI} hidden />
              {TimerWidget}
              {PageCarousel}
            </div>
          )}

          {!isFullscreen && (
            <button onClick={toggleFullscreen}
              className="absolute top-0 right-0 z-[9999] px-5 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-700">
              Fullscreen
            </button>
          )}

          <div className="flex flex-1 overflow-hidden min-h-0">
            {showImagePanel && isFullscreen && (
              <WhiteboardImagePanel
                excalidrawAPI={excalidrawAPI}
                onDragStart={(img) => { pendingDragImageRef.current = img; }}
                onClose={() => setShowImagePanel(false)}
              />
            )}
            <div className="flex-1 relative">
              <Excalidraw
                ref={(api) => {
                  excalidrawRef.current = api;
                  if (api && api !== excalidrawAPI) setExcalidrawAPI(api);
                }}
                onChange={() => setUnsavedChanges(true)}
                onImageAction={handleImageUpload}
              />
            </div>
          </div>
        </div>
      </div>
      {showTemplates && (
        <WhiteboardTemplates
          excalidrawAPI={excalidrawAPI}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* ── Save Modal ── */}
      {showSaveModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">💾 Save Board</h2>
            <label className="block text-sm font-medium text-gray-700 mb-1">Board Title</label>
            <input
              autoFocus
              type="text"
              value={modalTitle}
              onChange={e => setModalTitle(e.target.value)}
              placeholder="Enter board title"
              className="w-full border rounded px-3 py-2 text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 text-sm rounded border hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  handleSave(false, modalTitle);
                }}
                className="px-4 py-2 text-sm rounded bg-blue-500 hover:bg-blue-600 text-white">
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Share / Save & Assign Modal ── */}
      {showShareModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">📤 Save & Assign</h2>
            <label className="block text-sm font-medium text-gray-700 mb-1">Board Title</label>
            <input
              autoFocus
              type="text"
              value={modalTitle}
              onChange={e => setModalTitle(e.target.value)}
              placeholder="Enter board title"
              className="w-full border rounded px-3 py-2 text-sm mb-3"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Form</label>
            <select
              value={modalForm}
              onChange={e => setModalForm(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-4">
              <option value="">-- Form --</option>
              {forms.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 text-sm rounded border hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  handleSave(true, modalTitle, modalForm);
                }}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white">
                Save & Assign
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Unsaved Changes Modal ── */}
      {showUnsavedModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-2">⚠️ Unsaved Changes</h2>
            <p className="text-sm text-gray-600 mb-5">
              You have unsaved changes on this board. What would you like to do?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  setShowUnsavedModal(false);
                  setModalTitle(title);
                  setShowSaveModal(true);
                }}
                className="w-full px-4 py-2.5 text-sm rounded bg-blue-500 hover:bg-blue-600 text-white font-medium">
                💾 Save before leaving
              </button>
              <button
                onClick={() => { setShowUnsavedModal(false); resetToNewBoard(); }}
                className="w-full px-4 py-2.5 text-sm rounded bg-red-500 hover:bg-red-600 text-white">
                Discard changes and start new board
              </button>
              <button
                onClick={() => setShowUnsavedModal(false)}
                className="w-full px-4 py-2.5 text-sm rounded border hover:bg-gray-50 text-gray-700">
                Cancel — keep working
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default WhiteboardTab;
